import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  CartDecoratorErrorHandlingService,
  CPQ_ERROR_CODE_ELIGIBLE_FOR_RETRY,
  ERROR_CODE_CHECKOUT_UNAVAILABLE,
  ERROR_CODE_CUSTOMER_HAS_ALREADY_CPEA,
  ERROR_CODE_CUSTOMER_HAS_ALREADY_PAYG,
  ERROR_CODE_PRODUCT_NOT_ALLOWED_B2I,
  ERROR_CODE_RESTRUCTURE,
} from '@app/checkout/services/cart-decorator-error-handling.service';
import {DcpCartDecoratorService} from '@app/services/dcp-cart-decorator.service';
import {
  DcpError,
  DcpErrorHandlingUtil,
  DCPErrorPageDisplayEvent,
  DcpGlobalMessageService,
  DcpPriceData,
} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {EventService, TranslationService} from '@spartacus/core';
import {DefaultConfiguratorErrorComponent} from '../default-configurator-error/default-configurator-error.component';
import {AddCompanyDetailsErrorComponent} from '../net-new/add-company-details-error/add-company-details-error.component';
import {AddToExistingContractErrorComponent} from '../net-new/add-to-existing-contract-error/add-to-existing-contract-error.component';
import {CheckoutUnavailableComponent} from '../net-new/checkout-unavailable/checkout-unavailable.component';
import {ConfigureRestructureErrorComponent} from '../net-new/configure-restructure-error/configure-restructure-error.component';
import {NewContractErrorComponent} from '../net-new/new-contract-error/new-contract-error.component';
import {first} from 'rxjs';

@UntilDestroy()
@Component({
    selector: 'dcp-configure-service-error',
    templateUrl: './configure-service-error.component.html',
    standalone: false
})
export class ConfigureServiceErrorComponent implements AfterViewInit, OnDestroy {
  @Output() configureServiceError: EventEmitter<DcpError> = new EventEmitter<DcpError>();
  @Output() setHeader: EventEmitter<string> = new EventEmitter<string>();

  @Input() modalHeaderDisplay = false;

  @ViewChild('errorComponent', {static: false, read: ViewContainerRef}) errorComponentRef: ViewContainerRef;
  heading: string;

  constructor(
    private cartDecoratorErrorHandlingService: CartDecoratorErrorHandlingService,
    private dcpCartDecoratorService: DcpCartDecoratorService,
    private cd: ChangeDetectorRef,
    private dcpGlobalMessageService: DcpGlobalMessageService,
    private eventService: EventService,
    private translationService: TranslationService
  ) {}

  hasError: boolean = false;

  public getPricingError(pd: DcpPriceData): DcpError {
    return {
      hasError: !!pd?.price?.errorCode,
      isEligibleForRetry: pd?.price?.errorCode === CPQ_ERROR_CODE_ELIGIBLE_FOR_RETRY,
      errorCode: pd?.price?.errorCode,
      description: pd?.price?.errorMessage,
    };
  }

  private emitDisplayError(error: DcpError): void {
    this.hasError = error.hasError;
    this.configureServiceError.emit(error);
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    if (this.errorComponentRef) {
      this.errorComponentRef.clear();
    }
  }

  ngAfterViewInit() {
    this.cartDecoratorErrorHandlingService
      .getErrorState$()
      .pipe(untilDestroyed(this))
      .subscribe((error) => {
        this.emitDisplayError(error);
        this.loadComponent(error);
      });

    this.dcpCartDecoratorService.priceDataSubject.pipe(untilDestroyed(this)).subscribe((priceData) => {
      const error: DcpError = this.getPricingError(priceData);
      this.emitDisplayError(error);
      this.loadComponent(error);
    });
  }

  async loadComponent(error: DcpError) {
    if (error.hasError && this.errorComponentRef) {
      this.dcpGlobalMessageService.addMessage(error, this?.constructor?.name, 'errorLogging');
      this.errorComponentRef.clear();
      const component: any = this.selectErrorComponent(error?.errorCode);

      if (this.modalHeaderDisplay) {
        const headerChunk = this.selectErrorHeadline(error?.errorCode);
        this.setHeading(headerChunk);
        this.setHeader.emit(this.heading);
      }

      this.setComponent(component, error);
    }
  }

  setHeading(translationKey: string) {
    this.translationService
      .translate(translationKey)
      .pipe(first(), untilDestroyed(this))
      .subscribe((heading) => {
        return (this.heading = heading);
      });
  }

  setComponent(component, error: DcpError) {
    const ref: ComponentRef<any> = this.errorComponentRef.createComponent(component);
    ref.instance.error = error;
    ref.instance.titleDisplay = this.modalHeaderDisplay;
    ref.changeDetectorRef.detectChanges();
    this.publishEvent(error, component.name);
  }

  private publishEvent(error: any, pageName: string) {
    const internalError = DcpErrorHandlingUtil.mapToInternalError(error);
    this.eventService.dispatch(new DCPErrorPageDisplayEvent(internalError, pageName));
  }

  selectErrorHeadline(errorCode: string): any {
    switch (errorCode) {
      case ERROR_CODE_PRODUCT_NOT_ALLOWED_B2I: {
        return 'configurator.error.addCompanyDetails.headline';
      }
      case ERROR_CODE_CUSTOMER_HAS_ALREADY_CPEA: {
        return 'configurator.error.addToContract.headline';
      }
      case ERROR_CODE_CUSTOMER_HAS_ALREADY_PAYG: {
        return 'configurator.error.newContract.headline';
      }
      case ERROR_CODE_CHECKOUT_UNAVAILABLE: {
        return 'configurator.error.checkoutUnavailable.headline';
      }
      case ERROR_CODE_RESTRUCTURE: {
        return 'configurator.error.restructure.headline';
      }
      default: {
        return 'configurator.error.somethingWrong';
      }
    }
  }

  selectErrorComponent(errorCode: string): any {
    switch (errorCode) {
      case ERROR_CODE_PRODUCT_NOT_ALLOWED_B2I: {
        return AddCompanyDetailsErrorComponent;
      }
      case ERROR_CODE_CUSTOMER_HAS_ALREADY_CPEA: {
        return AddToExistingContractErrorComponent;
      }
      case ERROR_CODE_CUSTOMER_HAS_ALREADY_PAYG: {
        return NewContractErrorComponent;
      }
      case ERROR_CODE_CHECKOUT_UNAVAILABLE: {
        return CheckoutUnavailableComponent;
      }
      case ERROR_CODE_RESTRUCTURE: {
        return ConfigureRestructureErrorComponent;
      }
      default: {
        return DefaultConfiguratorErrorComponent;
      }
    }
  }
}
